import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/common/Header';
import Snackbar from '../components/common/Snackbar';
import useSnackbar from '../hooks/useSnackbar';
import { getStoreById, approveStore } from '../services/storeService';
import { getAdminData } from '../services/authService';
import { getStatusClass, getStatusLabel } from '../utils/storeStatus';
import { formatDate } from '../utils/dateFormat';
import { IMAGES } from '../constants/images';
import '../css/StoreManagement.css';
import '../css/StoreDetail.css';
import StoreDecisionModal from '../components/common/StoreDecisionModal';

const StoreDetail = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminInfo, setAdminInfo] = useState(null);
  const [decisionModalOpen, setDecisionModalOpen] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState(false);

  useEffect(() => {
    setAdminInfo(getAdminData());
  }, []);

  const fetchStoreDetail = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getStoreById(storeId);
      setStore(result);
    } catch (error) {
      showSnackbar(error.message || '가맹점 정보를 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [storeId, showSnackbar]);

  useEffect(() => {
    fetchStoreDetail();
  }, [fetchStoreDetail]);

  const renderValue = (value) => {
    if (React.isValidElement(value)) return value;
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    return value;
  };

  const fullAddress = [store?.address, store?.address_detail].filter(Boolean).join(' ');

  const renderRatingStars = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = Number(value);
    if (Number.isNaN(num)) {
      return value;
    }

    const clamped = Math.max(0, Math.min(5, num));

    return (
      <span className="rating-stars">
        {[1, 2, 3, 4, 5].map((index) => (
          <span key={index} className={`star ${clamped >= index ? 'filled' : ''}`}>
            ★
          </span>
        ))}
        <span className="rating-score">({clamped.toFixed(1)}/5)</span>
      </span>
    );
  };

  const storeInfoRows = [
    [
      { label: '가맹점 이름', value: store?.store_name },
      { label: '매장 고유번호', value: store?.store_id },
    ],
    [
      { label: '가맹점 전화번호', value: store?.phone },
      { label: '별점', value: renderRatingStars(store?.rating) },
    ],
    [
      { label: '가맹점 등록일', value: formatDate(store?.created_at) },
      { label: '가맹점 수정일', value: formatDate(store?.updated_at) },
    ],
    [{ label: '주소', value: fullAddress, fullRow: true }],
  ];

  const businessInfoRows = [
    [
      { label: '사업자등록 상호', value: store?.store_name },
      { label: '사업자등록 번호', value: store?.business_number },
    ],
    [
      { label: '대표자명', value: store?.owner_name },
      { label: '휴대전화번호', value: store?.owner_phone },
    ],
    [{ label: '영업상태', value: getStatusLabel(store?.status), fullRow: true }],
    [
      {
        label: '사업자등록증',
        value: store?.business_registration_url ? (
          <a
            href={store.business_registration_url}
            target="_blank"
            rel="noreferrer"
            className="link-text"
            onClick={(e) => e.stopPropagation()}
          >
            서류 보기
          </a>
        ) : (
          '-'
        ),
        fullRow: true,
      },
    ],
    [{ label: '반려 사유', value: store?.reject_reason, fullRow: true }],
  ];

  const renderTableRows = (rows) =>
    rows.map((row, rowIndex) => (
      <tr key={`row-${rowIndex}`}>
        {row.length === 1 && row[0].fullRow ? (
          <React.Fragment>
            <th>{row[0].label}</th>
            <td colSpan={3}>{renderValue(row[0].value)}</td>
          </React.Fragment>
        ) : (
          row.flatMap((cell, cellIndex) => [
            <th key={`row-${rowIndex}-cell-${cellIndex}-label`}>{cell.label}</th>,
            <td key={`row-${rowIndex}-cell-${cellIndex}-value`}>{renderValue(cell.value)}</td>,
          ])
        )}
      </tr>
    ));

  const handleDecision = async (approved, reason = '') => {
    if (!approved && !reason.trim()) {
      showSnackbar('반려 사유를 입력해주세요.', 'error');
      return;
    }

    setDecisionLoading(true);
    try {
      await approveStore(storeId, {
        approved,
        rejectReason: approved ? undefined : reason.trim(),
      });
      showSnackbar(approved ? '승인 완료되었습니다.' : '반려 처리되었습니다.', 'success');
      setDecisionModalOpen(false);
      fetchStoreDetail();
    } catch (error) {
      showSnackbar(error.message || '요청 처리에 실패했습니다.', 'error');
    } finally {
      setDecisionLoading(false);
    }
  };

  return (
    <div className="store-detail">
      <Header />

      <div className="store-detail-container">
        <div className="store-detail-header">
          <div className="store-detail-heading">
            <button className="back-icon" onClick={() => navigate(-1)} aria-label="뒤로가기">
              <img src={IMAGES.BACK} alt="뒤로가기" />
            </button>
            <div className="store-detail-title">
              <h1>{store?.store_name || '가맹점 상세'}</h1>
              {store && (
                <span
                  className={`status-badge ${getStatusClass(store.status)} ${
                    store.status === 'PENDING' ? 'clickable' : ''
                  }`}
                  onClick={store?.status === 'PENDING' ? () => setDecisionModalOpen(true) : undefined}
                >
                  {getStatusLabel(store.status)}
                </span>
              )}
            </div>
          </div>

          <div className="store-detail-actions">
            <button
              type="button"
              className="primary-action-btn"
              onClick={() => navigate(`/admin/management/stores/${storeId}/seats`)}
              style={{
                background: '#F85009',
                color: '#fff',
                border: 'none',
                padding: '8px 16px',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              좌석 배치도 관리
            </button>
            <button
              type="button"
              className="primary-action-btn"
              onClick={() => navigate(`/admin/management/stores/${storeId}/photos`)}
              style={{
                background: '#fff',
                color: '#F85009',
                border: '1px solid #F85009',
                padding: '8px 16px',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              매장 사진 관리
            </button>
            <div className="admin-pill">{adminInfo?.name || '관리자'}</div>
          </div>
        </div>

        {loading ? (
          <div className="store-detail-card loading-card">로딩 중...</div>
        ) : !store ? (
          <div className="store-detail-card empty-card">가맹점 정보를 찾을 수 없습니다.</div>
        ) : (
          <>
            <section className="store-detail-card">
              <div className="card-header">
                <h2>가맹점 정보</h2>
              </div>
              <table className="detail-table">
                <tbody>{renderTableRows(storeInfoRows)}</tbody>
              </table>
            </section>

            <section className="store-detail-card">
              <div className="card-header">
                <h2>사업자 정보</h2>
              </div>
              <table className="detail-table">
                <tbody>{renderTableRows(businessInfoRows)}</tbody>
              </table>
            </section>
          </>
        )}
      </div>

      <StoreDecisionModal
        isOpen={decisionModalOpen}
        storeName={store?.store_name}
        loading={decisionLoading}
        onClose={() => setDecisionModalOpen(false)}
        onSubmit={handleDecision}
      />

      <Snackbar
        message={snackbar.message}
        type={snackbar.type}
        isOpen={snackbar.isOpen}
        onClose={hideSnackbar}
        duration={3000}
      />
    </div>
  );
};

export default StoreDetail;
